// Fields of values a section shows in the left column (um/2026-10-30/03 п.7): the person picks a value there, the list narrows to it
// and to its children. The choice lives in the address (?term=), so it is a link and the column lights it.

import type { FieldDef, Filter, Id, MetaIndex, TermDef } from '@confdeck/core-shared';
import { pipelineSectionOf } from '../pipelines/pipeline-section.ts';
import { typeHref } from './router.ts';

/**
 * The taxonomy fields of a section the owner put into the column. The stage field of a section that lives in pipelines is not one:
 * its pipelines and branches are the column's items already (R-U29, decision 040 п.4 — no row «Статус»).
 */
export function navFields(meta: MetaIndex, typeId: Id): FieldDef[] {
  const sectioned = !!pipelineSectionOf(meta.type(typeId)?.key);
  return meta.fields(typeId).filter((f) => f.inNav && f.fieldType === 'taxonomy' && !f.archivedAt && !!meta.taxonomy(f.options.taxonomyId as Id)
    && !(sectioned && meta.taxonomy(f.options.taxonomyId as Id)?.style === 'pipeline'));
}

/**
 * The values to show under the field: the top terms, but the single root of a pipeline (the one pipeline of task statuses) is skipped —
 * its stages are what people choose between. A single plain value stays as it is.
 */
export function navTerms(meta: MetaIndex, field: FieldDef): TermDef[] {
  const tax = field.options.taxonomyId as Id;
  const top = meta.children(null, tax);
  const kids = top.length === 1 && meta.taxonomy(tax)?.style === 'pipeline' ? meta.children(top[0]!.id, tax) : [];
  return kids.length ? kids : top;
}

/** The field of the section whose values hold the term. */
export const termField = (meta: MetaIndex, typeId: Id, termId: string | null): FieldDef | undefined => {
  const t = termId ? meta.term(termId as Id) : undefined;
  return t ? navFields(meta, typeId).find((f) => f.options.taxonomyId === t.taxonomyId) : undefined;
};

/** The list's filter narrowed to the chosen value and its children. */
export function withTerm(filter: Filter | null, field: FieldDef | undefined, termId: string | null): Filter | null {
  if (!field || !termId) return filter;
  const cond: Filter = { field: field.key, op: 'under', value: termId };
  return filter ? { and: [filter, cond] } : cond;
}

export const termHref = (typeHrefBase: string, termId: string) => `${typeHrefBase}${typeHrefBase.includes('?') ? '&' : '?'}term=${termId}`;

/** The address of a list of a section: its view and the value chosen in the column, both kept when the other changes. */
export const listHref = (typeKey: string, viewId: string | null | undefined, termId: string | null | undefined, branch?: string | null) => {
  const base = termId ? termHref(typeHref(typeKey, viewId), termId) : typeHref(typeKey, viewId);
  return branch ? `${base}${base.includes('?') ? '&' : '?'}branch=${encodeURIComponent(branch)}` : base;
};

/**
 * What narrows the list besides the view's own filter: the value of the column and the branch of «Все» (R-U29). The table and the CSV
 * export use this one chain, so the file holds exactly the rows the table shows (J5); the event scope is added by each (the export
 * sends it apart).
 */
export function narrowList(base: Filter | null, term: { field: FieldDef | undefined; id: string | null }, branch: Filter | null = null): Filter | null {
  const f = withTerm(base, term.field, term.id);
  return branch ? (f ? { and: [f, branch] } : branch) : f;
}
